import {
  Modal,
  ModalActionButton,
  ModalActions,
  ModalCloseIconButton,
  ModalContent,
  ModalTitle,
  Typography,
} from '@ace-clublabs/shared-component-library';
import { PropsWithChildren, ReactNode } from 'react';
import DOMPurify from 'dompurify';

const TOOLTIP_BODY_HTML_CONFIG = {
  ALLOWED_TAGS: ['a', 'b', 'br', 'i', 'li', 'sup', 'ul'],
  ALLOWED_ATTR: ['href', 'rel', 'style', 'target'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):)/i,
};

const TOOLTIP_TITLE_HTML_CONFIG = {
  ...TOOLTIP_BODY_HTML_CONFIG,
  ALLOWED_TAGS: ['a', 'b', 'br', 'i', 'sup'],
};

const sanitizeTooltipBodyHtml = (value: string): string => DOMPurify.sanitize(value, TOOLTIP_BODY_HTML_CONFIG);
const sanitizeTooltipTitleHtml = (value: string): string => DOMPurify.sanitize(value, TOOLTIP_TITLE_HTML_CONFIG);

export interface TooltipModalContentProps extends PropsWithChildren {
  tooltipTitle: string;
}

export interface TooltipModalProps extends TooltipModalContentProps {
  showModal: boolean;
  closeModal: () => void;
}

export const TooltipModalSubContent = (props: { subtitle?: string; tooltipContent: string | ReactNode }) => {
  return (
    <>
      <ModalContent id="modal-tooltip-content">
        {props.subtitle ? <Typography variant="b1Bold">{props.subtitle}</Typography> : <></>}
        <Typography
          component="div"
          sx={{ marginBottom: '16px', whiteSpace: 'pre-line' }}
          className="tooltip-sub-content"
        >
          {typeof props.tooltipContent === 'string' ? (
            <div dangerouslySetInnerHTML={{ __html: sanitizeTooltipBodyHtml(props.tooltipContent) }} />
          ) : (
            props.tooltipContent
          )}
        </Typography>
      </ModalContent>
    </>
  );
};

export const TooltipModal: (props: TooltipModalProps) => JSX.Element = ({
  tooltipTitle,
  showModal,
  children,
  closeModal,
}) => {
  return (
    <Modal
      className="tooltip-modal"
      open={showModal}
      variants={{
        xs: 'bottom-sheet',
        sm: 'regular',
        md: 'regular',
        lg: 'regular',
        xl: 'regular',
      }}
      size="medium"
      aria-labelledby="remove-item"
      aria-describedby="remove-item"
      sx={{
        minHeight: '268px',
        '& .MuiDialogPaperVariant-bottom-sheet': {
          minHeight: '0 !important',
        },
      }}
    >
      <ModalTitle id="remove-modal-title" className={'qm-mask'} sx={{ paddingRight: '2.5rem', fontSize: '1.25rem' }}>
        <span dangerouslySetInnerHTML={{ __html: sanitizeTooltipTitleHtml(tooltipTitle) }} />
        <ModalCloseIconButton
          aria-label="Close"
          onClick={() => {
            closeModal();
          }}
        />
      </ModalTitle>
      {children}
      <ModalActions
        actionLayout={{
          sm: 'fullWidth',
          md: 'fullWidth',
          lg: 'rightAlign',
          xl: 'rightAlign',
        }}
      >
        <ModalActionButton
          onClick={() => {
            closeModal();
          }}
          label="Got it"
        />
      </ModalActions>
    </Modal>
  );
};
